import { useMemo, useState } from "react";
import { useTripStore } from "../store/tripStore";
import {
  collectUsedTimezoneIds,
  formatTimezoneLabel,
} from "../utils/timezoneDisplay";
import { TimezoneCatalogModal } from "./TimezoneCatalogModal";
import { Button } from "./Button";

export function TripTimezoneSelect({
  value,
  date,
  onChange,
  disabled = false,
  sameAsZone,
  id,
  "data-testid": dataTestId,
}: {
  value: string;
  date: string;
  onChange: (timezoneId: string) => void;
  disabled?: boolean;
  sameAsZone?: string;
  id?: string;
  "data-testid"?: string;
}) {
  const trip = useTripStore((state) => state.trip);
  const schedule = useTripStore((state) => state.schedule);
  const setTripTimezones = useTripStore((state) => state.setTripTimezones);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const effectiveDate = /^\d{4}-\d{2}-\d{2}$/.test(date)
    ? date
    : (trip?.startDate ?? "2000-01-01");

  const zones = useMemo(() => {
    if (!trip) return [value];
    return [
      ...new Set([
        trip.defaultTimezone,
        ...trip.timezoneIds,
        ...collectUsedTimezoneIds(trip.defaultTimezone, schedule),
        value,
      ]),
    ];
  }, [schedule, trip, value]);

  const selectFromCatalog = (timezoneId: string) => {
    if (trip && timezoneId !== trip.defaultTimezone) {
      setTripTimezones(trip.defaultTimezone, [...trip.timezoneIds, timezoneId]);
    }
    onChange(timezoneId);
    setCatalogOpen(false);
  };

  return (
    <>
      <div className="flex min-w-0 gap-2">
        <select
          id={id}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          disabled={disabled}
          data-testid={dataTestId}
          className="select-base min-w-0 flex-1 pr-8"
        >
          {zones.map((zone) => (
            <option key={zone} value={zone}>
              {sameAsZone === zone
                ? `Same as start — ${formatTimezoneLabel(zone, effectiveDate)}`
                : formatTimezoneLabel(zone, effectiveDate)}
            </option>
          ))}
        </select>
        {!disabled && (
          <Button
            variant="secondary"
            size="lg"
            icon="add"
            onClick={() => setCatalogOpen(true)}
            className="shrink-0 rounded-lg text-secondary hover:border-sky-300 hover:bg-sky-50 hover:text-info focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
            aria-label="Add timezone from full catalogue"
            title="Add timezone…"
          />
        )}
      </div>
      <TimezoneCatalogModal
        open={catalogOpen}
        date={effectiveDate}
        onClose={() => setCatalogOpen(false)}
        onSelect={selectFromCatalog}
      />
    </>
  );
}
